import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Divider
} from '@mui/material';
import ReactSelect from 'react-select';
import { useNavigate, useLocation } from 'react-router-dom';
import RichTextEditor from '../components/common/RichTextEditor';
import useNotification from '../hooks/useNotification';
import teamService from '../services/teamService';
import userService from '../services/userService';
import projectService from '../services/projectService';

/**
 * CreateTeam component
 * Form for creating or editing a team
 */
const CreateTeam = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [teamId, setTeamId] = useState(null);

  // Get teamId from query param if editing
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('teamId');
    if (id) {
      setIsEditMode(true);
      setTeamId(id);
    }
  }, [location]);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    owner: '',
    coOwners: [],
    projects: [],
    distributionEmail: '',
    customers: ''
  });

  // Form validation
  const [errors, setErrors] = useState({});

  // Fetch users and projects on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, projectsData] = await Promise.all([
          userService.getAllUsers(),
          projectService.getProjects()
        ]);

        // Process users to extract first/last names from name field
        const processedUsers = (usersData || []).map(user => {
          // Extract firstName and lastName from name field if available
          let firstName = '';
          let lastName = '';

          if (user.name) {
            const nameParts = user.name.split(' ');
            if (nameParts.length > 1) {
              firstName = nameParts[0];
              lastName = nameParts.slice(1).join(' ');
            } else {
              firstName = user.name;
            }
          }

          // Add extracted fields to user object
          return {
            ...user,
            firstName: user.firstName || firstName,
            lastName: user.lastName || lastName,
            // Add a formatted display name
            displayName: `${firstName} ${lastName}`.trim() || user.name || user.username || user.email || 'Unnamed User'
          };
        });

        console.log('Processed users with display names:', processedUsers.slice(0, 3));
        setUsers(processedUsers || []);
        setProjects(projectsData?.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        showNotification({
          message: 'Failed to fetch users or projects',
          type: 'error'
        });
      }
    };

    fetchData();
  }, [showNotification]);

  // Fetch team data if in edit mode
  useEffect(() => {
    const fetchTeamData = async () => {
      if (isEditMode && teamId) {
        try {
          // First try to get the specific team directly
          const response = await teamService.getTeamById(teamId);
          const teamData = response.data;

          if (teamData) {
            console.log('Processing team data for form:', teamData);

            // Handle Owner: Try the new field first, then fall back to members array
            let ownerId = '';
            if (teamData.owner) {
              // New schema: direct owner reference
              ownerId = typeof teamData.owner === 'object' ? teamData.owner._id : teamData.owner;
            } else if (teamData.members?.length > 0) {
              // Legacy: extract from members array
              const ownerMember = teamData.members.find(member => member.role === 'owner') || teamData.members[0];
              ownerId = ownerMember?.user?._id ||
                      (typeof ownerMember?.user === 'string' ? ownerMember.user : '');
            }

            // Handle Co-owners: Try the new field first, then fall back to members array
            let coOwnerIds = [];
            if (teamData.coOwners && Array.isArray(teamData.coOwners)) {
              // New schema: direct coOwners array
              coOwnerIds = teamData.coOwners.map(coOwner =>
                typeof coOwner === 'object' ? coOwner._id : coOwner
              );
            } else if (teamData.members?.length > 0) {
              // Legacy: extract from members array
              coOwnerIds = teamData.members
                ?.filter((member, index) =>
                  // Consider as co-owner if:
                  // 1. Role is admin (not the owner)
                  // 2. OR it's an additional owner role (not the first owner)
                  (member.role === 'admin' ||
                  (index > 0 && member.role === 'owner')))
                ?.map(member => member.user?._id || (typeof member.user === 'string' ? member.user : ''))
                ?.filter(id => id);
            }

            // Handle Customer: could be direct object or array
            let customerId = '';
            console.log('Processing customer data:', {
              customer: teamData.customer,
              customers: teamData.customers,
              type: typeof teamData.customer
            });

            if (teamData.customer) {
              // New structure: direct customer reference
              customerId = typeof teamData.customer === 'object'
                ? teamData.customer._id || teamData.customer.id || ''
                : teamData.customer;
            } else if (teamData.customers?.length > 0) {
              // Legacy: from customers array
              const firstCustomer = teamData.customers[0];
              customerId = typeof firstCustomer === 'object'
                ? firstCustomer._id || firstCustomer.id || ''
                : firstCustomer;
            }

            console.log('Resolved customerId:', customerId);

            setFormData({
              code: teamData.shortCode || teamData.code || '',  // Try both field names
              name: teamData.name || '',
              description: teamData.description || '',
              owner: ownerId,
              coOwners: coOwnerIds || [],
              projects: teamData.projects || [],
              distributionEmail: teamData.distributionEmail || '',
              customers: customerId
            });
          } else {
            // Try getting all teams as a fallback
            const teamsResponse = await teamService.getUserTeams();
            const allTeams = teamsResponse.data || [];
            const directTeamData = allTeams.find(team => team._id === teamId);

            if (!directTeamData) {
              throw new Error('Team not found in any source');
            }

            console.log('Processing fallback team data for form:', directTeamData);

            // Handle Owner: Try the new field first, then fall back to members array
            let ownerId = '';
            if (directTeamData.owner) {
              // New schema: direct owner reference
              ownerId = typeof directTeamData.owner === 'object' ? directTeamData.owner._id : directTeamData.owner;
            } else if (directTeamData.members?.length > 0) {
              // Legacy: extract from members array
              const ownerMember = directTeamData.members.find(member => member.role === 'owner') || directTeamData.members[0];
              ownerId = ownerMember?.user?._id ||
                      (typeof ownerMember?.user === 'string' ? ownerMember.user : '');
            }

            // Handle Co-owners: Try the new field first, then fall back to members array
            let coOwnerIds = [];
            if (directTeamData.coOwners && Array.isArray(directTeamData.coOwners)) {
              // New schema: direct coOwners array
              coOwnerIds = directTeamData.coOwners.map(coOwner =>
                typeof coOwner === 'object' ? coOwner._id : coOwner
              );
            } else if (directTeamData.members?.length > 0) {
              // Legacy: extract from members array
              coOwnerIds = directTeamData.members
                ?.filter((member, index) =>
                  // Consider as co-owner if:
                  // 1. Role is admin (not the owner)
                  // 2. OR it's an additional owner role (not the first owner)
                  (member.role === 'admin' ||
                  (index > 0 && member.role === 'owner')))
                ?.map(member => member.user?._id || (typeof member.user === 'string' ? member.user : ''))
                ?.filter(id => id);
            }

            // Handle Customer: could be direct object or array
            let customerId = '';
            console.log('Processing fallback customer data:', {
              customer: directTeamData.customer,
              customers: directTeamData.customers,
              type: typeof directTeamData.customer
            });

            if (directTeamData.customer) {
              // New structure: direct customer reference
              customerId = typeof directTeamData.customer === 'object'
                ? directTeamData.customer._id || directTeamData.customer.id || ''
                : directTeamData.customer;
            } else if (directTeamData.customers?.length > 0) {
              // Legacy: from customers array
              const firstCustomer = directTeamData.customers[0];
              customerId = typeof firstCustomer === 'object'
                ? firstCustomer._id || firstCustomer.id || ''
                : firstCustomer;
            }

            console.log('Resolved fallback customerId:', customerId);

            setFormData({
              code: directTeamData.shortCode || directTeamData.code || '',  // Try both field names
              name: directTeamData.name || '',
              description: directTeamData.description || '',
              owner: ownerId,
              coOwners: coOwnerIds || [],
              projects: directTeamData.projects || [],
              distributionEmail: directTeamData.distributionEmail || '',
              customers: customerId
            });
          }
        } catch (error) {
          console.error('Error fetching team data:', error);
          showNotification({
            message: 'Failed to fetch team data for editing. Please try again.',
            type: 'error'
          });
          // Redirect back to teams list on error
          navigate('/teams');
        }
      }
    };

    fetchTeamData();
  }, [isEditMode, teamId, showNotification, navigate]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle rich text editor changes
  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    }

    if (!formData.owner) {
      newErrors.owner = 'Team owner is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Sanitize form data to ensure proper types
      const coOwners = Array.isArray(formData.coOwners) ? formData.coOwners : [];
      const projects = Array.isArray(formData.projects) ? formData.projects : [];

      // Log form state before submission
      console.log('Form data to submit:', formData);

      // Prepare team data
      const teamData = {
        code: formData.code,
        shortCode: formData.code, // Use the same value for both fields for compatibility
        name: formData.name,
        description: formData.description,
        owner: formData.owner, // Primary owner
        coOwners: coOwners.length > 0 ? coOwners : undefined, // Array of co-owners
        projects: projects.length > 0 ? projects : undefined,
        distributionEmail: formData.distributionEmail || undefined,
        customer: formData.customers || undefined, // Single customer
        // For backward compatibility
        customers: formData.customers ? [formData.customers] : undefined
      };

      // Log what we're actually sending to the backend
      console.log('Team data to be sent to backend:', {
        ...teamData,
        customer: `Value: ${teamData.customer}, Type: ${typeof teamData.customer}`,
        customers: teamData.customers ?
          `Array with ${teamData.customers.length} items, first item: ${JSON.stringify(teamData.customers[0])}` :
          'undefined'
      });

      console.log('Submitting team data:', teamData);

      if (isEditMode && teamId) {
        // Update existing team
        await teamService.updateTeam(teamId, teamData);
        showNotification({
          message: 'Team updated successfully!',
          type: 'success'
        });
      } else {
        // Create new team
        await teamService.createTeam(teamData);
        showNotification({
          message: 'Team created successfully!',
          type: 'success'
        });
      }

      // Redirect to teams list
      navigate('/teams');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} team:`, error);
      showNotification({
        message: error.message || `Failed to ${isEditMode ? 'update' : 'create'} team`,
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 500,
          color: '#475569',
          mb: 3
        }}
      >
        {isEditMode ? 'Edit' : 'Create'} team
      </Typography>

      <Box sx={{
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        padding: '24px 0',
        mb: 4
      }}>
        <Box component="form" onSubmit={handleSubmit} sx={{
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            px: 3,
            gap: 4,
            mb: 3
          }}>
            {/* Left side */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="code"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Short code
                </Typography>
                <TextField
                  id="code"
                  name="code"
                  placeholder="Enter short code"
                  fullWidth
                  value={formData.code}
                  onChange={handleChange}
                  error={!!errors.code}
                  helperText={errors.code}
                  size="small"
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: errors.code ? '#f43f5e' : '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: errors.code ? '#f43f5e' : '#94a3b8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errors.code ? '#f43f5e' : '#475569',
                        borderWidth: '1px',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="name"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Team name <Box component="span" sx={{ color: '#f43f5e' }}>*</Box>
                </Typography>
                <TextField
                  id="name"
                  name="name"
                  placeholder="Enter team name"
                  fullWidth
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  size="small"
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: errors.name ? '#f43f5e' : '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: errors.name ? '#f43f5e' : '#94a3b8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errors.name ? '#f43f5e' : '#475569',
                        borderWidth: '1px',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="description"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Description
                </Typography>
                <Box sx={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <RichTextEditor
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    placeholder="Enter team description"
                  />
                </Box>
              </Box>
            </Box>

            {/* Divider for medium screens and up */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            {/* Right side */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="owner"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Team owner <Box component="span" sx={{ color: '#f43f5e' }}>*</Box>
                </Typography>
                <ReactSelect
                  id="owner"
                  name="owner"
                  options={users.map(user => ({
                    value: user._id || '',
                    label: user.displayName || user.name || 'Unnamed User'
                  }))}
                  value={(() => {
                    if (!formData.owner) return null;

                    // Handle case where owner might be an object or ID string
                    if (typeof formData.owner === 'object' && formData.owner !== null) {
                      return {
                        value: formData.owner._id || '',
                        label: formData.owner.displayName || formData.owner.name || 'Unnamed User'
                      };
                    } else {
                      // It's an ID string
                      const userObj = users.find(u => u._id === formData.owner);
                      return {
                        value: formData.owner,
                        label: userObj?.displayName || userObj?.name || String(formData.owner)
                      };
                    }
                  })()}
                  onChange={(selectedOption) => {
                    setFormData(prev => ({
                      ...prev,
                      owner: selectedOption ? selectedOption.value : ''
                    }));

                    // Clear error if field is changed
                    if (errors.owner) {
                      setErrors(prev => ({ ...prev, owner: '' }));
                    }
                  }}
                  placeholder="Select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.owner ? '#f43f5e' : '#e5e7eb',
                      borderRadius: '4px',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: errors.owner ? '#f43f5e' : '#94a3b8',
                      },
                    }),
                    singleValue: (baseStyles) => ({
                      ...baseStyles,
                      color: '#475569',
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    }),
                    menuPortal: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
                {errors.owner && (
                  <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {errors.owner}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="coOwners"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Team co-owners
                </Typography>
                <ReactSelect
                  id="coOwners"
                  name="coOwners"
                  isMulti
                  options={users.map(user => ({
                    value: user._id || '',
                    label: user.displayName || user.name || 'Unnamed User'
                  }))}
                  value={(Array.isArray(formData.coOwners) ? formData.coOwners : []).map(user => {
                    // Handle case where user might be an object or ID string
                    if (typeof user === 'object' && user !== null) {
                      return {
                        value: user._id || '',
                        label: user.displayName || user.name || 'Unnamed User'
                      };
                    } else {
                      // It's an ID string
                      const userObj = users.find(u => u._id === user);
                      return {
                        value: user,
                        label: userObj?.displayName || userObj?.name || String(user)
                      };
                    }
                  })}
                  onChange={(selectedOptions) => {
                    setFormData(prev => ({
                      ...prev,
                      coOwners: selectedOptions ? selectedOptions.map(option => option.value) : []
                    }));

                    // Clear error if field is changed
                    if (errors.coOwners) {
                      setErrors(prev => ({ ...prev, coOwners: '' }));
                    }
                  }}
                  placeholder="Select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.coOwners ? '#f43f5e' : '#e5e7eb',
                      borderRadius: '4px',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: errors.coOwners ? '#f43f5e' : '#94a3b8',
                      },
                    }),
                    multiValue: (baseStyles) => ({
                      ...baseStyles,
                      backgroundColor: '#f1f5f9',
                      borderRadius: '4px',
                    }),
                    multiValueLabel: (baseStyles) => ({
                      ...baseStyles,
                      color: '#475569',
                      fontSize: '14px',
                    }),
                    multiValueRemove: (baseStyles) => ({
                      ...baseStyles,
                      color: '#64748b',
                      '&:hover': {
                        backgroundColor: '#e2e8f0',
                        color: '#f43f5e',
                      },
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    }),
                    menuPortal: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
                {errors.coOwners && (
                  <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {errors.coOwners}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="projects"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Projects
                </Typography>
                <ReactSelect
                  id="projects"
                  name="projects"
                  isMulti
                  options={projects.map(project => ({
                    value: project._id || '',
                    label: project.name || 'Unnamed Project'
                  }))}
                  value={(Array.isArray(formData.projects) ? formData.projects : []).map(project => {
                    // Handle case where project might be an object or ID string
                    if (typeof project === 'object' && project !== null) {
                      return {
                        value: project._id || '',
                        label: project.name || 'Unnamed Project'
                      };
                    } else {
                      // It's an ID string
                      const projectObj = projects.find(p => p._id === project);
                      return {
                        value: project,
                        label: projectObj?.name || String(project)
                      };
                    }
                  })}
                  onChange={(selectedOptions) => {
                    setFormData(prev => ({
                      ...prev,
                      projects: selectedOptions ? selectedOptions.map(option => option.value) : []
                    }));

                    // Clear error if field is changed
                    if (errors.projects) {
                      setErrors(prev => ({ ...prev, projects: '' }));
                    }
                  }}
                  placeholder="Select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.projects ? '#f43f5e' : '#e5e7eb',
                      borderRadius: '4px',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: errors.projects ? '#f43f5e' : '#94a3b8',
                      },
                    }),
                    multiValue: (baseStyles) => ({
                      ...baseStyles,
                      backgroundColor: '#f1f5f9',
                      borderRadius: '4px',
                    }),
                    multiValueLabel: (baseStyles) => ({
                      ...baseStyles,
                      color: '#475569',
                      fontSize: '14px',
                    }),
                    multiValueRemove: (baseStyles) => ({
                      ...baseStyles,
                      color: '#64748b',
                      '&:hover': {
                        backgroundColor: '#e2e8f0',
                        color: '#f43f5e',
                      },
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    }),
                    menuPortal: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
                {errors.projects && (
                  <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {errors.projects}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="distributionEmail"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Distribution email
                </Typography>
                <TextField
                  id="distributionEmail"
                  name="distributionEmail"
                  type="email"
                  placeholder="Enter distribution email"
                  fullWidth
                  value={formData.distributionEmail}
                  onChange={handleChange}
                  error={!!errors.distributionEmail}
                  helperText={errors.distributionEmail}
                  size="small"
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: errors.distributionEmail ? '#f43f5e' : '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: errors.distributionEmail ? '#f43f5e' : '#94a3b8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errors.distributionEmail ? '#f43f5e' : '#475569',
                        borderWidth: '1px',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="customers"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Customers
                </Typography>
                <ReactSelect
                  id="customers"
                  name="customers"
                  options={users.map(user => ({
                    value: user._id || '',
                    label: user.displayName || user.name || 'Unnamed User'
                  }))}
                  value={(() => {
                    if (!formData.customers) return null;

                    // Handle case where customer might be an object or ID string
                    if (typeof formData.customers === 'object' && formData.customers !== null) {
                      return {
                        value: formData.customers._id || '',
                        label: formData.customers.displayName || formData.customers.name || 'Unnamed User'
                      };
                    } else {
                      // It's an ID string
                      const userObj = users.find(u => u._id === formData.customers);
                      return {
                        value: formData.customers,
                        label: userObj?.displayName || userObj?.name || String(formData.customers)
                      };
                    }
                  })()}
                  onChange={(selectedOption) => {
                    console.log('Customer selected:', selectedOption);
                    // Using a callback form of setState to ensure we have the latest state
                    setFormData(prevFormData => {
                      const newFormData = {
                        ...prevFormData,
                        customers: selectedOption ? selectedOption.value : ''
                      };
                      console.log('New form data after customer selection:', newFormData);
                      return newFormData;
                    });

                    // Clear error if field is changed
                    if (errors.customers) {
                      setErrors(prev => ({ ...prev, customers: '' }));
                    }
                  }}
                  placeholder="Select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.customers ? '#f43f5e' : '#e5e7eb',
                      borderRadius: '4px',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: errors.customers ? '#f43f5e' : '#94a3b8',
                      },
                    }),
                    singleValue: (baseStyles) => ({
                      ...baseStyles,
                      color: '#475569',
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    }),
                    menuPortal: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
                {errors.customers && (
                  <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {errors.customers}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#cf649a',
            color: 'white',
            textTransform: 'none',
            px: 4,
            py: 1,
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#b85586',
            },
            '&.Mui-disabled': {
              bgcolor: '#f1c1d7',
              color: 'white',
            },
          }}
        >
          {isEditMode ? 'Update' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateTeam;